import { Badge } from "@package/subframe/components/Badge";
import { DropdownMenu } from "@package/subframe/components/DropdownMenu";
import { Tabs } from "@package/subframe/components/Tabs";
import * as SubframeCore from "@subframe/core";
import { FeatherCheck, FeatherChevronDown } from "@subframe/core";
import { useRef } from "react";

import { CountBadge } from "@/components/ui/count-badge";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  PROFILE_TAB_LABELS,
  PROFILE_TAB_PANEL_ID,
  type ProfileTab,
  sectionPickerLabel,
} from "@/lib/people-profile-tabs";

interface ProfileSectionNavProps {
  tabs: ProfileTab[];
  current: ProfileTab;
  onChange: (tab: ProfileTab) => void;
  /** Shown beside Acknowledgements. */
  outstandingCount: number;
}

/** Tabs on wider screens; on phones a "Section" picker that keeps the count in view. */
export function ProfileSectionNav(props: ProfileSectionNavProps) {
  const isMobile = useIsMobile();
  return isMobile ? <SectionPicker {...props} /> : <TabList {...props} />;
}

function TabList({
  tabs,
  current,
  onChange,
  outstandingCount,
}: ProfileSectionNavProps) {
  const items = useRef<(HTMLDivElement | null)[]>([]);

  const select = (index: number) => {
    const tab = tabs[index];
    if (!tab) return;
    onChange(tab);
    items.current[index]?.focus();
  };

  // Subframe's tab item is a plain div, so the tab roles and arrow keys are added here.
  const handleKeyDown = (event: React.KeyboardEvent, index: number) => {
    switch (event.key) {
      case "ArrowRight":
        event.preventDefault();
        select((index + 1) % tabs.length);
        return;
      case "ArrowLeft":
        event.preventDefault();
        select((index - 1 + tabs.length) % tabs.length);
        return;
      case "Home":
        event.preventDefault();
        select(0);
        return;
      case "End":
        event.preventDefault();
        select(tabs.length - 1);
        return;
      case "Enter":
      case " ":
        event.preventDefault();
        select(index);
        return;
    }
  };

  return (
    <div className="w-full" data-testid="profile-tabs">
      <Tabs role="tablist" aria-label="Profile sections">
        {tabs.map((tab, index) => (
          <Tabs.Item
            key={tab}
            ref={(element) => {
              items.current[index] = element;
            }}
            role="tab"
            id={`profile-tab-${tab}`}
            aria-controls={PROFILE_TAB_PANEL_ID}
            aria-selected={tab === current}
            tabIndex={tab === current ? 0 : -1}
            active={tab === current}
            onClick={() => onChange(tab)}
            onKeyDown={(event) => handleKeyDown(event, index)}
          >
            {PROFILE_TAB_LABELS[tab]}
            {tab === "acknowledgements" && (
              <CountBadge count={outstandingCount} />
            )}
          </Tabs.Item>
        ))}
      </Tabs>
    </div>
  );
}

function SectionPicker({
  tabs,
  current,
  onChange,
  outstandingCount,
}: ProfileSectionNavProps) {
  return (
    <SubframeCore.DropdownMenu.Root>
      <SubframeCore.DropdownMenu.Trigger asChild={true}>
        <button
          type="button"
          data-testid="profile-section-picker"
          aria-label={sectionPickerLabel(
            PROFILE_TAB_LABELS[current],
            outstandingCount,
          )}
          className="flex w-full items-center justify-between gap-2 rounded-md border border-solid border-neutral-200 bg-default-background px-4 py-3 text-left"
        >
          <span className="flex min-w-0 flex-col items-start">
            <span className="text-caption-bold font-caption-bold uppercase text-subtext-color">
              Section
            </span>
            <span className="text-body-bold font-body-bold text-default-font">
              {PROFILE_TAB_LABELS[current]}
            </span>
          </span>
          <span className="flex shrink-0 items-center gap-2">
            {outstandingCount > 0 && (
              <Badge variant="warning">{outstandingCount} to sign</Badge>
            )}
            <FeatherChevronDown className="text-body text-subtext-color" />
          </span>
        </button>
      </SubframeCore.DropdownMenu.Trigger>
      <SubframeCore.DropdownMenu.Portal>
        <SubframeCore.DropdownMenu.Content
          side="bottom"
          align="start"
          sideOffset={4}
          asChild={true}
        >
          <DropdownMenu className="w-[var(--radix-dropdown-menu-trigger-width)]">
            {tabs.map((tab) => (
              <DropdownMenu.DropdownItem
                key={tab}
                icon={
                  <FeatherCheck
                    className={tab === current ? "" : "invisible"}
                  />
                }
                aria-current={tab === current ? "page" : undefined}
                onSelect={() => onChange(tab)}
              >
                {PROFILE_TAB_LABELS[tab]}
                {tab === "acknowledgements" && outstandingCount > 0
                  ? ` (${outstandingCount})`
                  : ""}
              </DropdownMenu.DropdownItem>
            ))}
          </DropdownMenu>
        </SubframeCore.DropdownMenu.Content>
      </SubframeCore.DropdownMenu.Portal>
    </SubframeCore.DropdownMenu.Root>
  );
}
